import { anzeigeName, type DashboardCoaching, type Stage } from '@/core/types'
import type { ViewConfig } from '@/core/views/viewConfig'
import { useApp } from '@/core/state/appStore'
import {
  COACHING_FILTER_CATEGORIES,
  excludedByCategoryFrom,
  filterCoachingsExtra,
  type CoachingViewFilter,
} from '@/core/lib/coachingFilter'
import { dedupeTextOptions } from '@/core/lib/viewInput'
import { descriptionFor } from '@/variants'
import { currentVariant } from '@/core/data/session'
import { formatDatum } from '@/core/lib/dates'

function stageIndexOf(stage: Stage): number {
  const columns = descriptionFor(currentVariant()).columns
  const i = columns.findIndex((c) => c.id === stage)
  return i === -1 ? columns.length : i
}

export const coachingViewConfig: ViewConfig<DashboardCoaching> = {
  rowKey: (c) => c.id,
  title: anzeigeName,
  cards: true,
  emptyText: "Keine Teilnehmenden",
  groupBy: (c) => c.stage,
  sortKeys: [
    { key: 'name', label: "Name", accessor: (c) => c.name ?? '' },
    { key: 'stage', label: "Status", accessor: (c) => stageIndexOf(c.stage) },
  ],
  searchFields: (c) => [c.name],
  filterCategories: COACHING_FILTER_CATEGORIES,
  excludedFor: (filter) => excludedByCategoryFrom(filter as CoachingViewFilter),

  extraFilter: (items, filter) => filterCoachingsExtra(items, filter as CoachingViewFilter | null | undefined),

  properties: [{ id: 'aufgabe', value: (c) => c.aufgabe ?? null }],
}

export const zurueckgezogenAm = (c: DashboardCoaching): string | null =>
  c.zurueckgezogen ? `zurückgezogen am ${formatDatum(c.zurueckgezogen.am)}` : null

export const zurueckgezogenViewConfig: ViewConfig<DashboardCoaching> = {
  ...coachingViewConfig,
  properties: [
    ...(coachingViewConfig.properties ?? []),
    { id: 'zurueckgezogen', value: zurueckgezogenAm },
  ],
}

export function coachingGroupOrder(): Stage[] {
  return descriptionFor(currentVariant()).columns.map((c) => c.id)
}

export function coachingGroupLabel(stageId: string): string {
  return descriptionFor(currentVariant()).columns.find((c) => c.id === stageId)?.dbName ?? stageId
}

export type CoachingFilterCatalogs = {
  coach: ReturnType<typeof dedupeTextOptions>
  kostentraeger: ReturnType<typeof dedupeTextOptions>
}

export function useCoachingFilterCatalogs(completeItems: readonly DashboardCoaching[]): CoachingFilterCatalogs {
  const { members } = useApp()
  const agencies = new Map<string, string>()
  for (const item of [...completeItems].sort((a, b) => a.id.localeCompare(b.id))) {
    if (item.kostentraegerId && !agencies.has(item.kostentraegerId)) agencies.set(item.kostentraegerId, item.kostentraeger)
  }
  return {
    coach: dedupeTextOptions(members.filter((member) => member.isCoach).map((member) =>
      [member.vorname, member.nachname].map((value) => (value ?? '').trim()).filter(Boolean).join(' '))),
    kostentraeger: dedupeTextOptions([...agencies.values()].sort((a, b) => a.localeCompare(b, 'de'))),
  }
}
